import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaSave, FaSpinner } from "react-icons/fa";
import dayjs from "dayjs";
import { SipCalendar, SipCheck, SipDropdown, SipInput, SipTextarea } from "../form/SipControls";

const STATUS_OPTIONS = [
  { value: "planning", label: "Planning" },
  { value: "terlaksana", label: "Terlaksana" },
  { value: "tertunda", label: "Tertunda" },
  { value: "batal", label: "Batal" },
];

const UNIT_OPTIONS = [
  { value: "it", label: "IT" },
  { value: "lab", label: "Lab" },
  { value: "sarpra", label: "Sarpra" },
];

const emptyForm = {
  kegiatan: "",
  tgl: "",
  end: "",
  lokasi: "",
  status: "planning",
  team_id: "",
  unit: "it",
  auto_status: true,
  catatan: "",
  keterangan: "",
};

function EditCoeModal({ open, item, teams = [], onClose, onSubmit, loading }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      kegiatan: item.kegiatan || "",
      tgl: item.tgl ? dayjs(item.tgl).format("YYYY-MM-DD") : "",
      end: item.end ? dayjs(item.end).format("YYYY-MM-DD") : "",
      lokasi: item.lokasi || "",
      status: item.status || "planning",
      team_id: item.team_id ? String(item.team_id) : "",
      unit: item.unit || "it",
      auto_status: !(item.auto_status === 0 || item.auto_status === false),
      catatan: item.catatan || "",
      keterangan: item.keterangan || "",
    });
    setErrors({});
  }, [open, item]);

  if (!open || !item) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.kegiatan.trim()) next.kegiatan = "Kegiatan wajib diisi";
    if (!form.tgl) next.tgl = "Tanggal mulai wajib diisi";
    if (!form.end) next.end = "Tanggal selesai wajib diisi";
    if (!form.lokasi.trim()) next.lokasi = "Lokasi wajib diisi";
    if (!form.team_id) next.team_id = "Team wajib dipilih";
    if (!form.unit) next.unit = "Unit wajib dipilih";
    if (!form.status) next.status = "Status wajib dipilih";
    if (form.status === "batal" && !form.keterangan.trim()) {
      next.keterangan = "Keterangan wajib diisi jika status batal";
    }
    if (form.tgl && form.end && dayjs(form.end).isBefore(dayjs(form.tgl))) {
      next.end = "Tanggal selesai tidak boleh sebelum tanggal mulai";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit({
      kegiatan: form.kegiatan.trim(),
      tgl: form.tgl,
      end: form.end,
      lokasi: form.lokasi.trim(),
      status: form.status,
      team_id: form.team_id,
      unit: form.unit,
      auto_status: form.auto_status,
      catatan: form.catatan || null,
      keterangan: form.keterangan || null,
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Update COE</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Hanya mengubah data yang sudah ada (pembuatan di SIP StelkPro)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kegiatan <span className="text-red-500">*</span>
              </label>
              <SipInput
                name="kegiatan"
                value={form.kegiatan}
                onChange={handleChange}
                placeholder="Nama kegiatan"
              />
              {errors.kegiatan && (
                <p className="text-xs text-red-500 mt-1">{errors.kegiatan}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Mulai <span className="text-red-500">*</span>
              </label>
              <SipCalendar name="tgl" value={form.tgl} onChange={handleChange} />
              {errors.tgl && (
                <p className="text-xs text-red-500 mt-1">{errors.tgl}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Selesai <span className="text-red-500">*</span>
              </label>
              <SipCalendar name="end" value={form.end} onChange={handleChange} />
              {errors.end && (
                <p className="text-xs text-red-500 mt-1">{errors.end}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lokasi <span className="text-red-500">*</span>
              </label>
              <SipInput
                name="lokasi"
                value={form.lokasi}
                onChange={handleChange}
                placeholder="Lokasi kegiatan"
              />
              {errors.lokasi && (
                <p className="text-xs text-red-500 mt-1">{errors.lokasi}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <SipDropdown name="status" value={form.status} onChange={handleChange} options={STATUS_OPTIONS} filter={false} />
              {errors.status && (
                <p className="text-xs text-red-500 mt-1">{errors.status}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit <span className="text-red-500">*</span>
              </label>
              <SipDropdown name="unit" value={form.unit} onChange={handleChange} options={UNIT_OPTIONS} filter={false} />
              {errors.unit && (
                <p className="text-xs text-red-500 mt-1">{errors.unit}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team <span className="text-red-500">*</span>
              </label>
              <SipDropdown
                name="team_id"
                value={form.team_id}
                onChange={handleChange}
                placeholder="Pilih team"
                options={teams.map((team) => ({
                  label: team.nama || team.nama_team || `Team ${team.id}`,
                  value: String(team.id),
                }))}
              />
              {errors.team_id && (
                <p className="text-xs text-red-500 mt-1">{errors.team_id}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <SipCheck
                  name="auto_status"
                  checked={!!form.auto_status}
                  onChange={handleChange}
                />
                Update status otomatis berdasarkan tanggal
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catatan Pelaksanaan
              </label>
              <SipTextarea
                name="catatan"
                value={form.catatan}
                onChange={handleChange}
                rows={2}
                placeholder="Catatan umum (opsional)"
              />
            </div>

            {form.status === "batal" && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keterangan Batal{" "}
                  <span className="text-red-500">*</span>
                </label>
                <SipTextarea
                  name="keterangan"
                  value={form.keterangan}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Alasan kegiatan tidak terlaksana"
                />
                {errors.keterangan && (
                  <p className="text-xs text-red-500 mt-1">{errors.keterangan}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
            >
              {loading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaSave />
              )}
              {loading ? "Menyimpan..." : "Update COE"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default EditCoeModal;
